/**
 * VenuePartnerApplyPage.jsx — public form for restaurants/cafés/bars
 * to sign up as HustleMeetups partner venues.
 *
 * Route: /venue-partners/apply
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Utensils, ArrowLeft, Send, CheckCircle2 } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function VenuePartnerApplyPage() {
  const [f, setF] = useState({
    venue_name: "", venue_type: "restaurant", cuisine: "",
    address: "", suburb: "", state: "QLD", postcode: "",
    contact_name: "", contact_email: "", contact_phone: "",
    seats_capacity: 40, private_room: false, quiet_section: false,
    offer_notes: "",
  });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => setF({
    ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/hustlemeet-venue-partners/apply`, f);
      setSent(true);
      toast.success("Thanks — we'll be in touch within 3 business days.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Application failed");
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="venue-partner-apply-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100"
    >
      <PlatformHeader brand={{ name: "HustleMeetups — Venues", to: "/meet" }}
                      showBookCta={false} showCart={false} />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/circles" className="text-xs text-stone-500 inline-flex items-center gap-1 mb-4 hover:underline">
          <ArrowLeft size={12}/> Back to Circles
        </Link>
        <header className="mb-6 flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
            <Utensils size={20}/>
          </div>
          <div>
            <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">Partner with us</p>
            <h1 className="mt-1 text-2xl md:text-3xl font-black">List your venue on HustleMeetups</h1>
            <p className="mt-1 text-sm text-stone-500 max-w-md">
              Turn slow nights into standing bookings. Every partner venue receives Circle dinners,
              Matchmaker rounds, and a dashboard with covers RSVP'd + dietary manifests.
            </p>
          </div>
        </header>

        {sent ? (
          <div
            data-testid="apply-thanks"
            className="rounded-2xl border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-6"
          >
            <CheckCircle2 size={28} className="text-emerald-500"/>
            <p className="mt-3 font-black">Application received.</p>
            <p className="mt-1 text-sm text-stone-600 dark:text-stone-300">
              An admin will approve within 3 business days. Once approved you'll get a login link
              to <code>/venue-terminal/&lt;your-vendor-id&gt;</code> where you can accept Circles
              and start filling those quiet nights.
            </p>
          </div>
        ) : (
          <div className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="text-xs font-black uppercase tracking-widest text-stone-500 flex flex-col gap-1">
                Venue name
                <input data-testid="apply-venue-name" value={f.venue_name} onChange={upd("venue_name")}
                       className="mt-1 rounded-lg border border-stone-200 dark:border-stone-700 px-3 py-2 text-sm bg-transparent"/>
              </label>
              <label className="text-xs font-black uppercase tracking-widest text-stone-500 flex flex-col gap-1">
                Venue type
                <select value={f.venue_type} onChange={upd("venue_type")}
                        className="mt-1 rounded-lg border border-stone-200 dark:border-stone-700 px-3 py-2 text-sm bg-transparent">
                  <option value="restaurant">Restaurant</option>
                  <option value="cafe">Café</option>
                  <option value="wine_bar">Wine bar</option>
                  <option value="cocktail_lounge">Cocktail lounge</option>
                  <option value="pub">Pub / gastropub</option>
                  <option value="private_club">Private club</option>
                </select>
              </label>
              <label className="text-xs font-black uppercase tracking-widest text-stone-500 flex flex-col gap-1">
                Cuisine / style
                <input data-testid="apply-cuisine" value={f.cuisine} onChange={upd("cuisine")}
                       placeholder="Italian, Modern Australian, …"
                       className="mt-1 rounded-lg border border-stone-200 dark:border-stone-700 px-3 py-2 text-sm bg-transparent"/>
              </label>
              <label className="text-xs font-black uppercase tracking-widest text-stone-500 flex flex-col gap-1">
                Seat capacity
                <input type="number" value={f.seats_capacity} onChange={upd("seats_capacity")}
                       className="mt-1 rounded-lg border border-stone-200 dark:border-stone-700 px-3 py-2 text-sm bg-transparent"/>
              </label>
              <label className="text-xs font-black uppercase tracking-widest text-stone-500 flex flex-col gap-1 sm:col-span-2">
                Street address
                <input data-testid="apply-address" value={f.address} onChange={upd("address")}
                       className="mt-1 rounded-lg border border-stone-200 dark:border-stone-700 px-3 py-2 text-sm bg-transparent"/>
              </label>
              <label className="text-xs font-black uppercase tracking-widest text-stone-500 flex flex-col gap-1">
                Suburb
                <input data-testid="apply-suburb" value={f.suburb} onChange={upd("suburb")}
                       className="mt-1 rounded-lg border border-stone-200 dark:border-stone-700 px-3 py-2 text-sm bg-transparent"/>
              </label>
              <label className="text-xs font-black uppercase tracking-widest text-stone-500 flex flex-col gap-1">
                State
                <select value={f.state} onChange={upd("state")}
                        className="mt-1 rounded-lg border border-stone-200 dark:border-stone-700 px-3 py-2 text-sm bg-transparent">
                  {"QLD NSW VIC WA SA TAS ACT NT".split(" ").map((s) =>
                    <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="text-xs font-black uppercase tracking-widest text-stone-500 flex flex-col gap-1">
                Postcode
                <input value={f.postcode} onChange={upd("postcode")}
                       className="mt-1 rounded-lg border border-stone-200 dark:border-stone-700 px-3 py-2 text-sm bg-transparent"/>
              </label>
              <label className="text-xs font-black uppercase tracking-widest text-stone-500 flex flex-col gap-1">
                Contact name
                <input value={f.contact_name} onChange={upd("contact_name")}
                       className="mt-1 rounded-lg border border-stone-200 dark:border-stone-700 px-3 py-2 text-sm bg-transparent"/>
              </label>
              <label className="text-xs font-black uppercase tracking-widest text-stone-500 flex flex-col gap-1">
                Contact email
                <input data-testid="apply-email" value={f.contact_email} onChange={upd("contact_email")}
                       className="mt-1 rounded-lg border border-stone-200 dark:border-stone-700 px-3 py-2 text-sm bg-transparent"/>
              </label>
              <label className="text-xs font-black uppercase tracking-widest text-stone-500 flex flex-col gap-1">
                Contact phone
                <input value={f.contact_phone} onChange={upd("contact_phone")}
                       className="mt-1 rounded-lg border border-stone-200 dark:border-stone-700 px-3 py-2 text-sm bg-transparent"/>
              </label>
            </div>

            <div className="flex flex-wrap gap-4 pt-2 text-sm">
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={f.private_room} onChange={upd("private_room")}/>
                Private room available
              </label>
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={f.quiet_section} onChange={upd("quiet_section")}/>
                Quiet section available
              </label>
            </div>

            <label className="text-xs font-black uppercase tracking-widest text-stone-500 flex flex-col gap-1">
              What can you offer partnered Circles?
              <textarea rows={3} value={f.offer_notes} onChange={upd("offer_notes")}
                        placeholder="e.g. Set menu, private table Tuesdays 6-9pm, comped first round, 10% off group bookings"
                        className="mt-1 rounded-lg border border-stone-200 dark:border-stone-700 px-3 py-2 text-sm bg-transparent"/>
            </label>

            <div className="flex justify-end">
              <Button
                data-testid="apply-submit-btn"
                disabled={busy || !f.venue_name || !f.contact_email}
                onClick={submit}
                className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black h-11 px-6"
              >
                <Send size={14} className="mr-1"/> {busy ? "Sending…" : "Send application"}
              </Button>
            </div>
          </div>
        )}
      </div>

      <SuiteFooter />
    </div>
  );
}
